import { ApiError, ApiNetworkError, type ProblemBody } from './errors';

const API_BASE = '/api/v1';

/**
 * Global state the client reads on every request. Populated by the auth store
 * during `login()` / `logout()` / `switchOrganization()`. Not a Pinia store to
 * avoid a circular dependency between the API client and the store that uses it.
 */
const state: {
  token: string | null;
  onUnauthorized: (() => void) | null;
  onOffline: ((online: boolean) => void) | null;
} = {
  token: null,
  onUnauthorized: null,
  onOffline: null,
};

export function setApiToken(token: string | null): void {
  state.token = token;
}

export function onApiUnauthorized(cb: () => void): void {
  state.onUnauthorized = cb;
}

export function onApiConnectivity(cb: (online: boolean) => void): void {
  state.onOffline = cb;
}

/** Simple ETag cache keyed by path. Lets `PATCH` send `If-Match` automatically. */
const etagCache = new Map<string, string>();

function cacheKey(method: string, path: string): string {
  return `${method}:${path}`;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  headers?: Record<string, string>;
  /**
   * If true, attach `If-Match: <etag>` using the cached ETag for the GET that
   * populated the same path. PATCH/PUT/DELETE default to true when an ETag
   * exists; override to false to skip the check.
   */
  useIfMatch?: boolean;
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  etag: string | null;
  requestId: string | null;
  status: number;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        for (const item of v) url.searchParams.append(k, String(item));
      } else {
        url.searchParams.append(k, String(v));
      }
    }
  }
  return url.pathname + url.search;
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const method = opts.method ?? 'GET';
  const url = buildUrl(path, opts.query);

  const headers: Record<string, string> = {
    accept: 'application/json',
    ...opts.headers,
  };
  if (state.token) headers.authorization = `Bearer ${state.token}`;
  if (opts.body !== undefined && headers['content-type'] === undefined) {
    headers['content-type'] = 'application/json';
  }

  // Attach If-Match when mutating something we previously loaded
  if (method !== 'GET' && opts.useIfMatch !== false && !headers['if-match']) {
    const etag = etagCache.get(cacheKey('GET', path));
    if (etag) headers['if-match'] = etag;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
    state.onOffline?.(true);
  } catch (err) {
    state.onOffline?.(false);
    throw new ApiNetworkError(err instanceof Error ? err.message : 'network error');
  }

  const etag = response.headers.get('etag');
  const requestId = response.headers.get('x-request-id');
  // Keep the If-Match cache fresh across mutations: PATCH/PUT return the
  // updated entity's ETag on the same path (a second rename/delete would
  // otherwise send the stale one and 412), and a successful DELETE removes
  // the entity, so its cached ETag must go too.
  if (method === 'GET' && etag) {
    etagCache.set(cacheKey('GET', path), etag);
  } else if (response.ok && etag && (method === 'PATCH' || method === 'PUT')) {
    etagCache.set(cacheKey('GET', path), etag);
  } else if (response.ok && method === 'DELETE') {
    etagCache.delete(cacheKey('GET', path));
  }

  if (response.status === 204) {
    return { data: undefined as T, etag, requestId, status: 204 };
  }

  const contentType = response.headers.get('content-type') ?? '';
  let body: unknown;
  try {
    if (contentType.includes('application/json') || contentType.includes('problem+json')) {
      body = await response.json();
    } else {
      body = await response.text();
    }
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const problem = (body as ProblemBody | undefined) ?? {
      type: 'about:blank',
      title: `HTTP ${response.status}`,
      status: response.status,
    };

    if (response.status === 401) {
      state.onUnauthorized?.();
    }

    throw new ApiError({
      status: response.status,
      title: problem.title ?? `HTTP ${response.status}`,
      detail: problem.detail,
      requestId: requestId ?? undefined,
      fieldErrors: problem.errors,
      retryAfterSeconds: (() => {
        const ra = response.headers.get('retry-after');
        return ra ? Number(ra) : undefined;
      })(),
      body: problem,
    });
  }

  return { data: body as T, etag, requestId, status: response.status };
}

/**
 * Convenience helpers for the common verbs — keeps call sites readable.
 */
export const api = {
  get: <T>(path: string, opts: Omit<RequestOptions, 'method'> = {}) =>
    apiRequest<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts: Omit<RequestOptions, 'method'> = {}) =>
    apiRequest<T>(path, { ...opts, method: 'DELETE' }),
};

/** Exposed for the orval-generated client's mutator integration. */
export async function apiFetch<T>(
  url: string,
  init: RequestInit & { body?: unknown; params?: RequestOptions['query'] } = {},
): Promise<T> {
  const path = url.replace(/^\/api\/v1/, '');
  const method = (init.method as RequestOptions['method']) ?? 'GET';
  const response = await apiRequest<T>(path, {
    method,
    body: init.body,
    query: init.params,
    headers: init.headers as Record<string, string> | undefined,
    signal: init.signal ?? undefined,
  });
  return response.data;
}
